import { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { analyticsService } from '@common/analytics';
import { favoriteAdvisorAPI, unFavoriteAdvisorAPI } from '@common/api/api';
import { log, LOG_LEVELS } from '../../../../../common/config/app_logger';
import { advisorUpdated } from '../../../../actions/shared_actions';

interface Advisor {
  id: number;
  conversionEventId?: string;
}

interface UseFavoriteAdvisorReturn {
  favorite: boolean;
  isLoading: boolean;
  error: Error | null;
  toggleFavorite: (isFavorite: boolean, clickSource: string) => Promise<void>;
  markFavorite: (clickSource: string) => Promise<void>;
  markUnfavorite: (clickSource: string) => Promise<void>;
}

export const useFavoriteAdvisor = (
  advisorId: number,
  initialFavorite: boolean = false
): UseFavoriteAdvisorReturn => {
  const dispatch = useDispatch();
  const [favorite, setFavorite] = useState<boolean>(initialFavorite);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => { setError(null); }, [advisorId]);
  useEffect(() => { setFavorite(initialFavorite); }, [initialFavorite]);

  const markFavorite = useCallback(async (clickSource: string): Promise<void> => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await favoriteAdvisorAPI(advisorId);
      if (response.error) {
        log(LOG_LEVELS.INFO, 'Favorite Advisors', 'Advisor already favorited', response.error);
        setFavorite(true);
        return;
      }
      const advisor: Advisor = response.advisor || response;
      const { conversionEventId } = advisor;
      analyticsService.trackEvent('favorite advisor', { 'advisor id': advisorId, 'click source': clickSource }, { conversionEventId });
      setFavorite(true);
      dispatch(advisorUpdated(advisor) as any);
    } catch (err) {
      log(LOG_LEVELS.ERROR, 'Favorite Advisors', 'ERROR markFavoriteAdvisor', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [advisorId]);

  const markUnfavorite = useCallback(async (clickSource: string): Promise<void> => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await unFavoriteAdvisorAPI(advisorId);
      analyticsService.trackEvent('unfavorite advisor', { 'advisor id': advisorId, 'click source': clickSource });
      setFavorite(false);
      dispatch(advisorUpdated(response) as any);
    } catch (err) {
      if (err.status === 404) {
        log(LOG_LEVELS.INFO, 'Favorite Advisors', 'Advisor not favorited (404)', err);
        setFavorite(false);
        return;
      }
      log(LOG_LEVELS.ERROR, 'Favorite Advisors', 'ERROR markUnfavoriteAdvisor', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [advisorId]);

  const toggleFavorite = useCallback(async (isFavorite: boolean, clickSource: string): Promise<void> => {
    if (isLoading) return;
    if (isFavorite) {
      await markUnfavorite(clickSource);
    } else {
      await markFavorite(clickSource);
    }
  }, [markFavorite, markUnfavorite, isLoading]);

  return {
    favorite,
    isLoading,
    error,
    toggleFavorite,
    markFavorite,
    markUnfavorite
  };
};
